//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import {
  FeedValidationError,
  IFeedValidationSummaryResponse,
  IInvalidSampleResponse,
  IValidationResultsResponse,
  IValidSampleResponse,
  VALIDATION_STATUS,
} from '@apis/catalog-validation/catalogValidationModel';
import {
  getCatalogValidation,
  getCatalogValidationInvalidSample,
  getCatalogValidationResults,
  getCatalogValidationStatus,
  getCatalogValidationValidSample,
  onboardCatalogValidation,
  scheduleCatalogValidation,
} from '@apis/catalog-validation/productCatalogValidation';
import { ConnectPanelActions } from '@catalog-ingestion/actions/connectPanelActions';
import { ThresholdFailedConfirmationModalActions } from '@catalog-ingestion/actions/thresholdFailedConfirmationModalActions';
import { CONNECTION_METHODS } from '@catalog-ingestion/constants/connectPanelConstants';
import {
  GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS,
  WORKFLOW_PANELS,
} from '@catalog-ingestion/constants/googleCatalogIngestionModalConstants';
import { ExpandingDataTableType } from '@catalog-ingestion/models/GoogleCatalogIngestionModalModel';
import { parseLocale } from '@catalog-ingestion/utils/parseLocale';
import { ICuralateStore } from '@models/AppState';
import ClientId from '@models/ClientId';
import { createAction } from '@utils/actions';
import { RequestError } from '@utils/sessionFetch';
import { ReduxDispatch } from 'app/curalate.store';

const resetGoogleCatalogIngestionState = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RESET_STATE,
  });
};

const openGoogleCatalogIngestionModal = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.OPEN_MODAL,
  });
};

const closeGoogleCatalogIngestionModal = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.CLOSE_MODAL,
  });
};

const changePanel = (newPanel: WORKFLOW_PANELS) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.CHANGE_PANEL,
    newPanel,
  });
};

const scheduleValidation = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.REQUEST_IS_SCHEDULING,
  });
};

const receiveIsScheduling = (validationToken: string) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_IS_SCHEDULING,
    validationToken,
  });
};

const requestValidationStatus = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.REQUEST_VALIDATION_STATUS,
  });
};

const receiveValidationStatus = (validationStatus: VALIDATION_STATUS) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_VALIDATION_STATUS,
    validationStatus,
  });
};

const requestValidationSummary = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.REQUEST_VALIDATION,
  });
};

const receiveValidationSummary = (
  validationData: IFeedValidationSummaryResponse,
) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_VALIDATION,
    validationData,
  });
};

const getValidSample = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.GET_VALID_SAMPLE,
  });
};

const receiveValidSample = (validSample: IValidSampleResponse) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_VALID_SAMPLE,
    validSample,
  });
};

const getInvalidSample = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.GET_INVALID_SAMPLE,
  });
};

const receiveInvalidSample = (invalidSample: IInvalidSampleResponse) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_INVALID_SAMPLE,
    invalidSample,
  });
};

const getValidationResults = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.GET_VALIDATION_RESULTS,
  });
};

const receiveValidationResults = (
  validationResults: IValidationResultsResponse,
) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.RECEIVE_VALIDATION_RESULTS,
    validationResults,
  });
};

const onboardCatalog = () => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.ONBOARD_CATALOG,
  });
};

const setCatalogOnboarded = (isOnboarded: boolean) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.CATALOG_ONBOARDED,
    isOnboarded,
  });
};

const setFullErrorData = (fullErrorData: string[][]) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.SET_FULL_ERROR_DATA,
    fullErrorData,
  });
};

const setExpandingErrorData = (expandingErrorData: ExpandingDataTableType) => {
  return createAction({
    type: GOOGLE_CATALOG_INGESTION_MODAL_ACTIONS.SET_EXPANDING_ERROR_DATA,
    expandingErrorData,
  });
};

const getNextPanel =
  () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
    const state = getState().catalogIngestionReducers;
    switch (state.googleCatalogIngestionModal.currentPanel) {
      case WORKFLOW_PANELS.CONNECT_PANEL:
        // validation bar screen
        if (state.connectPanel.isValidating) {
          // if the catalog is validated switch to the review panel
          if (
            state.googleCatalogIngestionModal.validationStatus ===
            VALIDATION_STATUS.COMPLETE
          ) {
            dispatch(changePanel(WORKFLOW_PANELS.REVIEW_PANEL));
            dispatch(ConnectPanelActions.toggleIsValidating());
          }
          // catalog is not validating (connect screen with option to upload file or connect catalog with url)
        } else {
          // ---- file upload case ----
          if (
            state.connectPanel.connectionMethod ===
            CONNECTION_METHODS.FileUpload
          ) {
            // if there is a catalog url, the upload has finished and we can start validating the catalog.
            if (state.connectPanel.catalogUrl) {
              dispatch(ConnectPanelActions.toggleIsValidating());
              // if we don't have a catalog url, there are a few cases we need to handle
              // 1) The user has not tried to upload a file (handled in the logic below)
              // 2) The upload has not completed (there will be displayed info based on isFileUploading bool)
              // 3) The upload failed (error will show up based on boolean set in ConnectPanelActions.uploadFileToS3)
            } else {
              // if there is no catalogUrl, the file isn't uploading, and there wasn't an error uploading the file,
              // the user hasn't tried to upload a file yet.
              if (
                !state.connectPanel.isFileUploading &&
                !state.connectPanel.didFileUploadFail
              ) {
                dispatch(ConnectPanelActions.setIsUrlEmpty(true));
              }
            }
            // ---- catalog url upload case ----
          } else {
            // has the user entered a catalog url
            if (state.connectPanel.catalogUrl) {
              // validate the source of the catalog
              const areCatalogCredentialsValid = await dispatch(
                ConnectPanelActions.validateSource(),
              );
              // if the source is valid, continue on to validate the catalog
              if (areCatalogCredentialsValid) {
                dispatch(ConnectPanelActions.toggleIsValidating());
                dispatch(ConnectPanelActions.setIsCatalogSourceInvalid(false));
                // otherwise, show a warning that the source isn't valid
              } else {
                dispatch(ConnectPanelActions.setIsCatalogSourceInvalid(true));
              }
            } else {
              dispatch(ConnectPanelActions.setIsUrlEmpty(true));
            }
          }
        }
        break;
      case WORKFLOW_PANELS.REVIEW_PANEL:
        if (
          state.googleCatalogIngestionModal.isThresholdPassed ||
          state.thresholdFailedConfirmationModal.isOpen
        ) {
          dispatch(changePanel(WORKFLOW_PANELS.PROCESS_PANEL));
        } else {
          dispatch(
            ThresholdFailedConfirmationModalActions.openThresholdFailedConfirmationModal(),
          );
        }
        break;
      case WORKFLOW_PANELS.PROCESS_PANEL:
        if (state.googleCatalogIngestionModal.isCatalogOnboarded) {
          dispatch(closeGoogleCatalogIngestionModal());
        }
        break;
    }
  };

export type GoogleCatalogIngestionModalAction = ReturnType<
  | typeof resetGoogleCatalogIngestionState
  | typeof openGoogleCatalogIngestionModal
  | typeof closeGoogleCatalogIngestionModal
  | typeof changePanel
  | typeof scheduleValidation
  | typeof receiveIsScheduling
  | typeof requestValidationStatus
  | typeof receiveValidationStatus
  | typeof requestValidationSummary
  | typeof receiveValidationSummary
  | typeof getValidSample
  | typeof receiveValidSample
  | typeof getInvalidSample
  | typeof receiveInvalidSample
  | typeof getValidationResults
  | typeof receiveValidationResults
  | typeof onboardCatalog
  | typeof setCatalogOnboarded
  | typeof setFullErrorData
  | typeof setExpandingErrorData
>;

export const GoogleCatalogIngestionModalActions = {
  resetGoogleCatalogIngestionState,
  openGoogleCatalogIngestionModal,
  closeGoogleCatalogIngestionModal,
  changePanel,
  getNextPanel,
  receiveIsScheduling,
  receiveValidationStatus,
  requestValidationSummary,
  receiveValidationSummary,
  receiveValidSample,
  receiveInvalidSample,
  receiveValidationResults,
  setCatalogOnboarded,
  setFullErrorData,
  setExpandingErrorData,

  resetAndCloseWorkflow: () => {
    window.location.assign('/');
  },

  scheduleValidation:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const catalogUrlCredentialsState =
        getState().catalogIngestionReducers.connectPanel;
      const clientIdState = getState().session.client.id;
      const catalogUrl = catalogUrlCredentialsState.catalogUrl;
      const password = catalogUrlCredentialsState.password;
      const login = catalogUrlCredentialsState.loginName;

      const { languageCode, countryCode } = parseLocale(
        getState().catalogIngestionReducers.connectPanel.selectedLocale,
      );

      const clientId: ClientId = ClientId(parseInt(clientIdState, 10));

      dispatch(scheduleValidation());
      const { token } = await scheduleCatalogValidation({
        catalogUrl,
        clientId,
        countryCode,
        languageCode,
        login,
        password,
      });
      dispatch(receiveIsScheduling(token));
    },

  requestValidationStatus:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const catalogIngestionState =
        getState().catalogIngestionReducers.googleCatalogIngestionModal;

      if (!catalogIngestionState.flags.isRetrievingStatus) {
        dispatch(requestValidationStatus());
        const { status } = await getCatalogValidationStatus(
          catalogIngestionState.validationToken,
        );
        dispatch(receiveValidationStatus(status));
      }
    },

  getValidSample:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const validationToken =
        getState().catalogIngestionReducers.googleCatalogIngestionModal
          .validationToken;

      dispatch(getValidSample());
      const validSampleJSON = await getCatalogValidationValidSample(
        validationToken,
      );
      dispatch(receiveValidSample(validSampleJSON));
    },

  getInvalidSample:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const validationToken =
        getState().catalogIngestionReducers.googleCatalogIngestionModal
          .validationToken;

      dispatch(getInvalidSample());
      const invalidSample = await getCatalogValidationInvalidSample(
        validationToken,
      );
      dispatch(receiveInvalidSample(invalidSample));
    },

  getValidationResults:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const validationToken =
        getState().catalogIngestionReducers.googleCatalogIngestionModal
          .validationToken;

      // get the validation summary
      dispatch(requestValidationSummary());
      const feedValidation = await getCatalogValidation(validationToken);
      dispatch(receiveValidationSummary(feedValidation));

      // make the structure for the error data that will be downloaded if the user clicks `download errors`
      const productsWithErrors: string[][] = [
        [
          'Row',
          'Product Id',
          'Product Title',
          'Product Link',
          'Product Image Link',
        ],
      ];

      // make the structure of the expanding data tables
      const feedValidationErrors: ExpandingDataTableType = Object.keys(
        FeedValidationError,
      ).reduce((accumulator, key) => {
        accumulator[key] = [];
        return accumulator;
      }, {});

      // init the array that will hold all of the results data
      const pages = Math.ceil(
        feedValidation.feedValidation.summary.processedRows / 100,
      );
      const pageList = new Array(pages);
      for (let i = 0; i < pages; i++) {
        pageList[i] = i;
      }

      // get all of the results, not just the first 100
      const promises = pageList.map((pageNumber) => {
        dispatch(getValidationResults());
        return getCatalogValidationResults(validationToken, pageNumber);
      });

      // await the promises and iterate through them in order
      const results = await Promise.all(promises);
      results.forEach((resultList) => {
        receiveValidationResults(resultList);
        // build the error data
        resultList.results.forEach((result) => {
          const failureReasons = result.failureReasons;
          if (failureReasons.length) {
            const failedProduct: string[] = [
              result.rowNumber.toString() || '',
              result.feedId || 'ERROR',
              result.title || 'ERROR',
              result.link || 'ERROR',
              result.imageLink || 'ERROR',
            ];
            productsWithErrors.push(failedProduct);
            failureReasons.forEach((failureReason) => {
              if (
                feedValidationErrors[failureReason] &&
                feedValidationErrors[failureReason].length < 10
              ) {
                feedValidationErrors[failureReason].push({
                  errorType: failureReason,
                  sample: result,
                });
              }
            });
          }
        });
      });

      dispatch(setExpandingErrorData(feedValidationErrors));
      dispatch(setFullErrorData(productsWithErrors));
    },

  onboardCatalog:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const validationToken =
        getState().catalogIngestionReducers.googleCatalogIngestionModal
          .validationToken;

      dispatch(onboardCatalog());
      try {
        await onboardCatalogValidation(validationToken);
        dispatch(setCatalogOnboarded(true));
      } catch (e) {
        if (e instanceof RequestError && e.statusCode === 500) {
          dispatch(setCatalogOnboarded(false));
        }
      }
    },
};
